import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { DAYS } from "@/lib/constants";
import { Printer, Loader2, Check, ShieldCheck, Ban, RotateCcw, Clock, CheckCircle2, Pencil, Trash2, X, BookOpen } from "lucide-react";
import PublisherCard from "@/features/PublisherCard";

const NAVY = "#0B1B3F";

const CELL = {
  Bekliyor: "bg-white border-slate-200",
  Tamamlandı: "bg-[#FEF3C7] border-amber-300",
  Onaylı: "bg-[#D1FAE5] border-emerald-300",
  Yapılmadı: "bg-rose-100 border-rose-300",
};

const LEGEND = [
  { label: "Bekliyor", dot: "bg-white border border-slate-300" },
  { label: "Tamamlandı", dot: "bg-amber-300" },
  { label: "Koç Onaylı", dot: "bg-emerald-400" },
  { label: "Yapılmadı", dot: "bg-rose-400" },
];

export default function StudyProgram({ studentId, readOnly, track }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCell, setOpenCell] = useState(null);
  const [cellForm, setCellForm] = useState({ correct: "", wrong: "", blank: "" });
  const [pubFilter, setPubFilter] = useState("");
  const [dragOver, setDragOver] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [weekEdit, setWeekEdit] = useState(null);
  const [showPubCard, setShowPubCard] = useState(false);

  const delWeek = async (w, count) => {
    if (!window.confirm(`${w}. haftadaki ${count} görevin tamamı öğrencinin programından silinecek. Emin misiniz?`)) return;
    try {
      const { data } = await api.delete(`/weekly-schedule/${studentId}/week/${w}`, { params: pubFilter ? { publisher: pubFilter } : {} });
      toast.success(`${w}. hafta silindi (${data.deleted} görev)`);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const saveWeekEdit = async () => {
    try {
      const { data } = await api.put(`/weekly-schedule/${studentId}/week/${weekEdit.week}/edit`, {
        new_week: Number(weekEdit.new_week || weekEdit.week), publisher: weekEdit.publisher, resource_name: weekEdit.resource_name,
        status: weekEdit.status, reset_results: weekEdit.reset_results, publisher_filter: pubFilter,
      });
      toast.success(`${weekEdit.week}. hafta güncellendi (${data.updated} görev)`);
      setWeekEdit(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const isApprover = user?.role === "coach" || user?.role === "admin";
  const isStudent = user?.role === "student";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/weekly-schedule/${studentId}`);
      setItems(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (studentId) load();
  }, [load, studentId]);

  const setStatus = async (item, status) => {
    try {
      await api.put(`/weekly-schedule/${studentId}/${item.id}`, { ...item, status });
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const complete = async (it, status = "Tamamlandı") => {
    try {
      await api.put(`/weekly-schedule/${studentId}/${it.id}`, {
        ...it,
        correct: Number(cellForm.correct || 0),
        wrong: Number(cellForm.wrong || 0),
        blank: Number(cellForm.blank || 0),
        status,
      });
      toast.success(status === "Onaylı" ? "Kaydedildi ve onaylandı" : "Tamamlandı olarak işaretlendi");
      setOpenCell(null);
      setCellForm({ correct: "", wrong: "", blank: "" });
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const moveItem = async (it, week, day) => {
    if (Number(it.week || 1) === week && it.day === day) return;
    try {
      await api.put(`/weekly-schedule/${studentId}/${it.id}/move`, { week, day });
      toast.success(`Görev ${week}. hafta ${day} gününe taşındı`);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const approveWeek = async (w) => {
    try {
      const { data } = await api.put(`/weekly-schedule/${studentId}/week/${w}/approve`);
      toast.success(data.approved ? `${data.approved} görev onaylandı` : "Onay bekleyen görev yok");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const markWeekUndone = async (w) => {
    try {
      const { data } = await api.put(`/weekly-schedule/${studentId}/week/${w}/mark-undone`);
      toast.success(data.marked ? `${data.marked} görev 'Yapılmadı' işaretlendi` : "Bekleyen görev yok");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const saveEdit = async () => {
    if (!editItem) return;
    try {
      await api.put(`/weekly-schedule/${studentId}/${editItem.id}`, {
        ...editItem,
        total: Number(editItem.total || 0),
        correct: Number(editItem.correct || 0),
        wrong: Number(editItem.wrong || 0),
        blank: Number(editItem.blank || 0),
      });
      toast.success("Görev güncellendi");
      setEditItem(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const delItem = async (it) => {
    if (!window.confirm(`"${it.unit_name || (it.unit_no ? it.unit_no + ". Ünite" : "Görev")}" silinsin mi?`)) return;
    try {
      await api.delete(`/weekly-schedule/${studentId}/${it.id}`);
      toast.success("Görev silindi");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const pubOptions = [...new Set(items.map((i) => i.publisher).filter(Boolean))];
  const view = pubFilter ? items.filter((i) => i.publisher === pubFilter) : items;
  const weeks = [...new Set(view.map((i) => Number(i.week || 1)))].sort((a, b) => a - b);

  return (
    <div className="space-y-6" data-testid="study-program">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-semibold text-slate-400 flex items-center gap-1.5">Kaynak Bazlı Haftalık Plan</div>
          <h3 className="font-display text-4xl font-extrabold text-slate-900 tracking-tight mt-1">Çalışma Programı</h3>
          <p className="text-sm text-slate-500 mt-1.5">
            Kutucuğa tıklayıp doğru/yanlış/boş gir ve durumu işaretle. Koç onaylayınca yeşil, yapılmadıysa kırmızı olur.
          </p>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          {pubOptions.length > 0 && (
            <select className="ta-input w-auto text-sm font-semibold" value={pubFilter} onChange={(e) => setPubFilter(e.target.value)} data-testid="study-publisher-filter">
              <option value="">Tüm Yayınevleri</option>
              {pubOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          )}
          <div className="flex items-center gap-3 text-xs text-slate-500">
            {LEGEND.map((l) => (
              <span key={l.label} className="flex items-center gap-1.5">
                <span className={`w-3 h-3 rounded-full ${l.dot}`} /> {l.label}
              </span>
            ))}
          </div>
          <Button variant="outline" className={showPubCard ? "border-indigo-900 bg-indigo-900 text-white hover:bg-indigo-800 hover:text-white" : "border-slate-300"} onClick={() => setShowPubCard((v) => !v)} data-testid="study-pubcard-btn">
            <BookOpen size={15} /> <span className="ml-1">Yayın Karnesi</span>
          </Button>
          <Button variant="outline" className="border-slate-300" onClick={() => window.print()} data-testid="study-print-btn">
            <Printer size={15} /> <span className="ml-1">Yazdır / PDF</span>
          </Button>
        </div>
      </div>

      {showPubCard && <PublisherCard studentId={studentId} />}

      {!loading && weeks.length > 0 && (
        <div className="font-display text-xl font-extrabold text-slate-900 uppercase" data-testid="study-program-meta">
          {track || "Genel Program"}
          <span className="text-sm font-semibold text-slate-500 normal-case"> · {weeks.length} hafta</span>
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>
      ) : weeks.length === 0 ? (
        <div className="card-ta p-12 text-center text-slate-500">Henüz program yok. "Haftalık Program" sekmesinden ders ekleyin.</div>
      ) : (
        weeks.map((w) => {
          const wItems = view.filter((i) => Number(i.week || 1) === w);
          const rowKey = (i) => `${i.subject}||${i.publisher || i.resource_name || ""}`;
          const subjects = [...new Set(wItems.map(rowKey))];
          const done = wItems.filter((i) => i.status === "Tamamlandı" || i.status === "Onaylı").length;
          const pendingApprove = wItems.filter((i) => i.status === "Tamamlandı").length;
          const pendingWait = wItems.filter((i) => i.status === "Bekliyor").length;
          const wTot = wItems.reduce((a, i) => ({ t: a.t + Number(i.total || 0), c: a.c + Number(i.correct || 0), wr: a.wr + Number(i.wrong || 0), b: a.b + Number(i.blank || 0) }), { t: 0, c: 0, wr: 0, b: 0 });
          return (
            <div key={w} className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm" data-testid={`study-week-${w}`}>
              <div className="text-white px-5 py-3.5 flex items-center justify-between" style={{ background: done === wItems.length && wItems.length > 0 ? "#065f46" : NAVY }}>
                <span className="font-display font-extrabold tracking-wide">{w}. HAFTA</span>
                <span className="flex items-center gap-2">
                  {isApprover && !readOnly && (
                    <>
                      <button onClick={() => setWeekEdit({ week: w, new_week: w, publisher: "", resource_name: "", status: "", reset_results: false })} data-testid={`study-edit-week-${w}`}
                        className="text-xs font-bold bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded-full transition-colors flex items-center gap-1">
                        <Pencil size={11} /> Haftayı Düzelt
                      </button>
                      <button onClick={() => delWeek(w, wItems.length)} data-testid={`study-del-week-${w}`}
                        className="text-xs font-bold bg-rose-600/90 hover:bg-rose-600 text-white px-3 py-1 rounded-full transition-colors flex items-center gap-1">
                        <Trash2 size={11} /> Haftayı Sil
                      </button>
                    </>
                  )}
                  {isApprover && !readOnly && pendingApprove > 0 && (
                    <button onClick={() => approveWeek(w)} data-testid={`study-approve-week-${w}`}
                      className="text-xs font-bold bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded-full transition-colors">
                      Tümünü Onayla ({pendingApprove})
                    </button>
                  )}
                  {isApprover && !readOnly && pendingWait > 0 && (
                    <button onClick={() => markWeekUndone(w)} data-testid={`study-undone-week-${w}`}
                      className="text-xs font-bold bg-rose-500/80 hover:bg-rose-500 text-white px-3 py-1 rounded-full transition-colors">
                      Kalanları Yapılmadı ({pendingWait})
                    </button>
                  )}
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${done === wItems.length && wItems.length > 0 ? "bg-white text-emerald-700" : "bg-green-500 text-white"}`} data-testid={`study-week-done-${w}`}>
                    {done === wItems.length && wItems.length > 0 ? "✓ Hafta tamamlandı" : `${done}/${wItems.length} tamamlandı`}
                  </span>
                </span>
              </div>
              <div className="overflow-x-auto bg-white">
                <table className="w-full text-sm border-collapse min-w-[950px]">
                  <thead>
                    <tr>
                      <th className="text-white text-left px-5 py-3 text-xs font-bold tracking-wider w-48" style={{ background: NAVY }}>DERSLER</th>
                      {DAYS.map((d) => (
                        <th key={d} className="text-white px-2 py-3 text-xs font-bold tracking-wider text-center" style={{ background: NAVY }}>{d.toUpperCase()}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {subjects.map((rk) => {
                      const sub = rk.split("||")[0];
                      const firstRow = wItems.find((i) => rowKey(i) === rk);
                      const res = firstRow?.resource_name;
                      const pub = firstRow?.publisher;
                      return (
                        <tr key={rk} className="border-t border-slate-200">
                          <td className="px-5 py-4 align-top" style={{ background: NAVY }}>
                            <div className="font-bold text-white text-sm uppercase tracking-wide">{sub}</div>
                            {pub && <div className="text-[10px] font-bold text-amber-300 mt-1 uppercase truncate" data-testid={`study-row-pub-${w}-${sub}`}>{pub}</div>}
                            {res && res !== pub && <div className="text-[11px] text-slate-400 mt-0.5">{res}</div>}
                          </td>
                          {DAYS.map((d) => {
                            const cellItems = wItems.filter((i) => rowKey(i) === rk && (i.day === d || (!i.day && d === "Pazartesi")));
                            return (
                              <td key={d} className={`align-top p-1.5 border-l border-slate-100 min-w-[125px] transition-colors ${dragOver === `${w}-${sub}-${d}` ? "bg-indigo-100 ring-2 ring-inset ring-indigo-400" : ""}`}
                                onDragOver={(e) => { if (isApprover && !readOnly) { e.preventDefault(); setDragOver(`${w}-${sub}-${d}`); } }}
                                onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(null); }}
                                onDrop={(e) => {
                                  setDragOver(null);
                                  if (!isApprover || readOnly) return;
                                  e.preventDefault();
                                  const id = e.dataTransfer.getData("text/plain");
                                  const it = items.find((x) => x.id === id);
                                  if (it) moveItem(it, w, d);
                                }}
                                data-testid={`study-drop-${w}-${sub}-${d}`}>
                                {cellItems.length === 0 ? (
                                  <div className="text-center text-slate-300 text-xs py-7">—</div>
                                ) : (
                                  <div className="space-y-1.5">
                                    {cellItems.map((it) => {
                                      const stt = it.status || "Bekliyor";
                                      const isFoy = it.section_type === "Föy" || it.resource_name?.includes("(Föy)");
                                      const isSB = it.section_type === "Soru Bankası" || it.resource_name?.includes("(Soru Bankası)");
                                      const isDeneme = it.section_type === "Deneme" || it.resource_name?.includes("(Deneme)");
                                      return (
                                        <div
                                          key={it.id}
                                          draggable={isApprover && !readOnly}
                                          onDragStart={(e) => { if (isApprover && !readOnly) { e.dataTransfer.setData("text/plain", it.id); e.dataTransfer.effectAllowed = "move"; } }}
                                          className={`rounded-xl border p-3 text-center transition-shadow ${CELL[stt] || CELL.Bekliyor} ${isApprover && !readOnly ? "cursor-grab active:cursor-grabbing" : ""} ${!readOnly && (isStudent || isApprover) && (stt === "Bekliyor" || stt === "Yapılmadı") ? "hover:shadow-md" : ""}`}
                                          data-testid={`study-cell-${it.id}`}
                                          onClick={() => {
                                            if (!readOnly && (isStudent || isApprover) && (stt === "Bekliyor" || stt === "Yapılmadı")) {
                                              setOpenCell(openCell === it.id ? null : it.id);
                                              setCellForm({ correct: it.correct ?? "", wrong: it.wrong ?? "", blank: it.blank ?? "" });
                                            }
                                          }}
                                        >
                                          {isApprover && !readOnly && (
                                            <div className="flex justify-end gap-1 mb-1" onClick={(e) => e.stopPropagation()}>
                                              <button onClick={() => setEditItem({ ...it })} className="text-slate-300 hover:text-amber-600" title="Düzenle" data-testid={`study-edit-${it.id}`}><Pencil size={12} /></button>
                                              <button onClick={() => delItem(it)} className="text-slate-300 hover:text-rose-600" title="Sil" data-testid={`study-del-${it.id}`}><Trash2 size={12} /></button>
                                            </div>
                                          )}
                                          {it.publisher && (
                                            <div className="text-[10px] font-bold text-indigo-700 uppercase tracking-wide truncate mb-1">
                                              {it.publisher}
                                            </div>
                                          )}
                                          {(isFoy || isSB || isDeneme) && (
                                            <span className={`inline-block text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded mb-1 ${isDeneme ? "bg-rose-100 text-rose-700 border border-rose-200" : isFoy ? "bg-sky-100 text-sky-700 border border-sky-200" : "bg-amber-100 text-amber-700 border border-amber-200"}`}>
                                              {isDeneme ? "DENEME" : isFoy ? "FÖY" : "SORU BANKASI"}
                                            </span>
                                          )}
                                          <div className="text-sm font-extrabold text-slate-900">{it.unit_no ? `${it.unit_no}. ÜNİTE` : it.unit_name || "-"}</div>
                                          {it.unit_no && it.unit_name && <div className="text-[10px] text-slate-500">{it.unit_name}</div>}
                                          {it.pages && <div className="text-[10px] font-semibold tracking-wide text-slate-600 mt-0.5">SAYFA {it.pages}</div>}
                                          {it.total > 0 && <div className="text-xs font-semibold text-slate-700 mt-1">Hedef {it.total} soru</div>}
                                          <div className="text-[11px] font-mono font-bold mt-1 flex items-center justify-center gap-1.5" data-testid={`study-result-${it.id}`}>
                                            <span className="text-emerald-600">D{it.correct || 0}</span>
                                            <span className="text-rose-600">Y{it.wrong || 0}</span>
                                            <span className="text-slate-500">B{it.blank || 0}</span>
                                          </div>
                                          {stt === "Tamamlandı" && <div className="mt-2 flex justify-center"><Clock className="text-amber-500" size={17} /></div>}
                                          {stt === "Onaylı" && (
                                            <div className="mt-2 flex justify-center text-emerald-600">
                                              <CheckCircle2 size={18} />
                                            </div>
                                          )}
                                          {stt === "Yapılmadı" && (
                                            <div className="mt-2 flex justify-center text-rose-600">
                                              <Ban size={17} />
                                            </div>
                                          )}
                                          {openCell === it.id && !readOnly && (isStudent || isApprover) && (stt === "Bekliyor" || stt === "Yapılmadı") && (
                                            <div className="mt-2 space-y-1.5" onClick={(e) => e.stopPropagation()}>
                                              <div className="grid grid-cols-3 gap-1">
                                                {["correct", "wrong", "blank"].map((f) => (
                                                  <input key={f} type="number" className="ta-input px-1 py-1 text-[11px] text-center" placeholder={{ correct: "D", wrong: "Y", blank: "B" }[f]}
                                                    value={cellForm[f]} onChange={(e) => setCellForm({ ...cellForm, [f]: e.target.value })} data-testid={`cell-${f}-${it.id}`} />
                                                ))}
                                              </div>
                                              {isStudent ? (
                                                <Button size="sm" className="w-full h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700" onClick={() => complete(it)} data-testid={`study-complete-${it.id}`}>
                                                  <Check size={12} /> <span className="ml-1">Tamamladım</span>
                                                </Button>
                                              ) : (
                                                <Button size="sm" className="w-full h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700" onClick={() => complete(it, "Onaylı")} data-testid={`study-admin-save-${it.id}`}>
                                                  <ShieldCheck size={12} /> <span className="ml-1">Kaydet & Onayla</span>
                                                </Button>
                                              )}
                                            </div>
                                          )}
                                          {isStudent && !readOnly && stt === "Tamamlandı" && (
                                            <button onClick={() => setStatus(it, "Bekliyor")} className="mt-1.5 text-[10px] text-slate-400 hover:text-slate-700 flex items-center gap-1 mx-auto">
                                              <RotateCcw size={10} /> Geri al
                                            </button>
                                          )}
                                          {isApprover && !readOnly && stt === "Tamamlandı" && (
                                            <div className="mt-2 flex gap-1">
                                              <Button size="sm" className="flex-1 h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700" onClick={() => setStatus(it, "Onaylı")} data-testid={`study-approve-${it.id}`}>
                                                <ShieldCheck size={12} /> Onayla
                                              </Button>
                                              <Button size="sm" className="flex-1 h-7 text-[11px] bg-rose-600 hover:bg-rose-700" onClick={() => setStatus(it, "Yapılmadı")} data-testid={`study-reject-${it.id}`}>
                                                <Ban size={12} /> Yapılmadı
                                              </Button>
                                            </div>
                                          )}
                                          {isApprover && !readOnly && stt === "Onaylı" && (
                                            <button onClick={() => setStatus(it, "Tamamlandı")} className="mt-1.5 text-[10px] text-slate-400 hover:text-slate-700 mx-auto flex items-center gap-1">
                                              <RotateCcw size={10} /> Onayı geri al
                                            </button>
                                          )}
                                          {isApprover && !readOnly && stt === "Yapılmadı" && (
                                            <button onClick={() => setStatus(it, "Bekliyor")} className="mt-1.5 text-[10px] text-slate-400 hover:text-slate-700 mx-auto flex items-center gap-1">
                                              <RotateCcw size={10} /> Bekliyor'a al
                                            </button>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="bg-slate-50 border-t border-slate-200 px-5 py-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs" data-testid={`study-week-totals-${w}`}>
                <span className="font-bold text-slate-700 uppercase tracking-wide">{w}. Hafta Toplamı</span>
                <span className="text-slate-500">Hedef: <b className="text-slate-800">{wTot.t}</b></span>
                <span className="text-slate-500">Toplam Soru: <b className="text-slate-800">{wTot.c + wTot.wr + wTot.b}</b></span>
                <span className="text-emerald-700">Doğru: <b>{wTot.c}</b></span>
                <span className="text-rose-700">Yanlış: <b>{wTot.wr}</b></span>
                <span className="text-slate-500">Boş: <b>{wTot.b}</b></span>
                <span className="text-indigo-700">Net: <b>{(wTot.c - wTot.wr / 4).toFixed(2)}</b></span>
              </div>
            </div>
          );
        })
      )}

      {weekEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setWeekEdit(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="study-week-edit-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-lg font-bold flex items-center gap-2"><Pencil size={17} className="text-amber-500" /> {weekEdit.week}. Haftayı Düzelt</h3>
              <button onClick={() => setWeekEdit(null)} className="text-slate-400 hover:text-slate-700" data-testid="study-week-edit-close"><X size={18} /></button>
            </div>
            <p className="text-xs text-slate-500 mb-4">Bu haftadaki tüm görevlere toplu uygulanır. Boş bıraktığınız alanlar değişmez.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="ta-label">Hafta No (taşı)</label>
                <input type="number" min="1" className="ta-input" value={weekEdit.new_week} onChange={(e) => setWeekEdit({ ...weekEdit, new_week: e.target.value })} data-testid="study-week-edit-newweek" />
              </div>
              <div>
                <label className="ta-label">Durum</label>
                <select className="ta-input" value={weekEdit.status} onChange={(e) => setWeekEdit({ ...weekEdit, status: e.target.value })} data-testid="study-week-edit-status">
                  <option value="">Değiştirme</option>
                  {["Bekliyor", "Tamamlandı", "Onaylı", "Yapılmadı"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="ta-label">Yayınevi</label>
                <input className="ta-input" value={weekEdit.publisher} onChange={(e) => setWeekEdit({ ...weekEdit, publisher: e.target.value })} placeholder="Değiştirme" data-testid="study-week-edit-publisher" />
              </div>
              <div>
                <label className="ta-label">Kaynak Adı</label>
                <input className="ta-input" value={weekEdit.resource_name} onChange={(e) => setWeekEdit({ ...weekEdit, resource_name: e.target.value })} placeholder="Değiştirme" data-testid="study-week-edit-resource" />
              </div>
              <label className="col-span-2 flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={weekEdit.reset_results} onChange={(e) => setWeekEdit({ ...weekEdit, reset_results: e.target.checked })} data-testid="study-week-edit-reset" />
                Doğru/Yanlış/Boş sonuçlarını sıfırla
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setWeekEdit(null)}>Vazgeç</Button>
              <Button onClick={saveWeekEdit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="study-week-edit-save">
                <Check size={15} /><span className="ml-1">Kaydet</span>
              </Button>
            </div>
          </div>
        </div>
      )}
      {editItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setEditItem(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="study-edit-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold flex items-center gap-2"><Pencil size={17} className="text-amber-500" /> Görevi Düzenle</h3>
              <button onClick={() => setEditItem(null)} className="text-slate-400 hover:text-slate-700" data-testid="study-edit-close"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="ta-label">Yayınevi</label>
                <input className="ta-input" value={editItem.publisher || ""} onChange={(e) => setEditItem({ ...editItem, publisher: e.target.value })} data-testid="study-edit-publisher" />
              </div>
              <div>
                <label className="ta-label">Ünite No</label>
                <input className="ta-input" value={editItem.unit_no || ""} onChange={(e) => setEditItem({ ...editItem, unit_no: e.target.value })} data-testid="study-edit-unitno" />
              </div>
              <div className="col-span-2">
                <label className="ta-label">Ünite / Konu Adı</label>
                <input className="ta-input" value={editItem.unit_name || ""} onChange={(e) => setEditItem({ ...editItem, unit_name: e.target.value })} data-testid="study-edit-unitname" />
              </div>
              <div>
                <label className="ta-label">Sayfa</label>
                <input className="ta-input" value={editItem.pages || ""} onChange={(e) => setEditItem({ ...editItem, pages: e.target.value })} data-testid="study-edit-pages" />
              </div>
              <div>
                <label className="ta-label">Durum</label>
                <select className="ta-input" value={editItem.status || "Bekliyor"} onChange={(e) => setEditItem({ ...editItem, status: e.target.value })} data-testid="study-edit-status">
                  {["Bekliyor", "Tamamlandı", "Onaylı", "Yapılmadı"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {[["total", "Toplam Soru"], ["correct", "Doğru"], ["wrong", "Yanlış"], ["blank", "Boş"]].map(([f, l]) => (
                <div key={f}>
                  <label className="ta-label">{l}</label>
                  <input type="number" className="ta-input text-center" value={editItem[f] ?? ""} onChange={(e) => setEditItem({ ...editItem, [f]: e.target.value })} data-testid={`study-edit-${f}`} />
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-slate-400">Soru sayılarını (Toplam/D/Y/B) buradan elle değiştirebilirsiniz; kaydedince tüm panellerde güncellenir.</p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditItem(null)}>Vazgeç</Button>
              <Button onClick={saveEdit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="study-edit-save">
                <Check size={15} /><span className="ml-1">Kaydet</span>
              </Button>
            </div>
          </div>
        </div>
      )}
      {!loading && weeks.length > 0 && (() => {
        const pubMap = {};
        view.forEach((i) => {
          const p = i.publisher;
          if (!p) return;
          pubMap[p] = pubMap[p] || { t: 0, c: 0, w: 0, b: 0, done: 0, count: 0 };
          const g = pubMap[p];
          g.t += Number(i.total || 0);
          g.c += Number(i.correct || 0);
          g.w += Number(i.wrong || 0);
          g.b += Number(i.blank || 0);
          g.count += 1;
          if (i.status === "Tamamlandı" || i.status === "Onaylı") g.done += 1;
        });
        const pubs = Object.entries(pubMap);
        if (!pubs.length) return null;
        return (
          <div className="space-y-3" data-testid="study-publisher-summary">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Yayınevi Bazlı Özet</div>
            {pubs.map(([pub, g]) => {
              const cards = [
                { label: "Toplam Soru", value: g.c + g.w + g.b, cls: "text-slate-900" },
                { label: "Doğru", value: g.c, cls: "text-emerald-600" },
                { label: "Yanlış", value: g.w, cls: "text-rose-600" },
                { label: "Boş", value: g.b, cls: "text-slate-500" },
                { label: "Net", value: (g.c - g.w / 4).toFixed(2), cls: "text-indigo-600" },
              ];
              return (
                <div key={pub} className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm" data-testid={`publisher-group-${pub}`}>
                  <div className="text-white px-5 py-3 flex items-center justify-between" style={{ background: NAVY }}>
                    <span className="font-display font-extrabold tracking-wide uppercase">{pub}</span>
                    <span className="text-xs font-bold bg-green-500 text-white px-3 py-1 rounded-full">{g.done}/{g.count} tamamlandı</span>
                  </div>
                  <div className="bg-white px-5 pt-4">
                    {(() => {
                      const pct = g.count ? Math.round((g.done / g.count) * 100) : 0;
                      return (
                        <div data-testid={`publisher-progress-${pub}`}>
                          <div className="flex items-center justify-between text-xs font-semibold text-slate-500 mb-1">
                            <span>İlerleme</span><span className="text-indigo-700">%{pct}</span>
                          </div>
                          <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="bg-white p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {cards.map((c) => (
                      <div key={c.label} className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3 text-center">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{c.label}</div>
                        <div className={`font-display text-2xl font-extrabold mt-1 ${c.cls}`}>{c.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {!loading && weeks.length > 0 && (() => {
        const g = view.reduce((a, i) => ({ t: a.t + Number(i.total || 0), c: a.c + Number(i.correct || 0), w: a.w + Number(i.wrong || 0), b: a.b + Number(i.blank || 0) }), { t: 0, c: 0, w: 0, b: 0 });
        const cards = [
          { label: "Hedef Soru", value: g.t, cls: "text-slate-900" },
          { label: "Toplam Soru", value: g.c + g.w + g.b, cls: "text-slate-900" },
          { label: "Doğru Sayısı", value: g.c, cls: "text-emerald-600" },
          { label: "Yanlış Sayısı", value: g.w, cls: "text-rose-600" },
          { label: "Boş Sayısı", value: g.b, cls: "text-slate-500" },
          { label: "Toplam Net", value: (g.c - g.w / 4).toFixed(2), cls: "text-indigo-600" },
        ];
        return (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5" data-testid="study-grand-total">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Program Genel Toplamı</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {cards.map((c) => (
                <div key={c.label} className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3 text-center">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{c.label}</div>
                  <div className={`font-display text-2xl font-extrabold mt-1 ${c.cls}`}>{c.value}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
